import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layout } from '@/components/Layout';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { PhotoThumb } from '@/components/PhotoThumb';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { adoptionApi } from '../api';
import { animalsPublicApi } from '@/features/public/animalsPublicApi';
import { useLookupsSnapshot, toOptionLabel } from '@/features/lookups/snapshot';
import type { AdoptionApplication, AdoptionApplicationTimeline, ApplicationStatus } from '../types';
import { APPLICATION_STATUS_LABELS } from '../types';
import { CalendarDays, PawPrint, Search, Settings2 } from 'lucide-react';
import { Timeline } from '../components/Timeline';
import { formatAnimalAge } from '@/lib/animalAge';

type ApplicationRow = AdoptionApplication & {
  animal?: {
    id: number;
    name?: string;
    photo?: string;
    species_label?: string;
    size_label?: string;
    age?: string;
    // New fields from backend
    sex?: { label?: string };
    approxAgeMonths?: number;
    color?: { label?: string };
    animalNeeds?: string;
  } | null;
  shelter?: {
    id: number;
    name?: string;
  } | null;
};

const STATUS_COLORS: Record<ApplicationStatus, string> = {
  SUBMITTED: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-100',
  IN_REVIEW: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-100',
  APPROVED: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-100',
  REJECTED: 'bg-rose-100 text-rose-800 dark:bg-rose-900/30 dark:text-rose-100',
  CANCELLED: 'bg-slate-200 text-slate-800 dark:bg-slate-900/40 dark:text-slate-100',
};

const KNOWN_LABELS: Record<string, string> = {
  reuse_animal_preferences: 'Preferencias de búsqueda aplicadas',
  review_acceptance: 'Acepto revisión de la información',
};

export function AdopterDashboardPage() {
  const navigate = useNavigate();
  const [applications, setApplications] = useState<ApplicationRow[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selected, setSelected] = useState<ApplicationRow | null>(null);
  const [timeline, setTimeline] = useState<AdoptionApplicationTimeline[]>([]);
  const [formResponse, setFormResponse] = useState<any | null>(null);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [isDetailLoading, setIsDetailLoading] = useState(false);
  const { data: lookupSnapshot } = useLookupsSnapshot();

  useEffect(() => {
    let cancelled = false;
    const toText = (value: any): string => {
      if (value === null || value === undefined) return '';
      if (typeof value === 'object') {
        return value.label ?? value.name ?? value.code ?? value.id ?? '';
      }
      return String(value);
    };

    const normalizeAnimal = (animal: any) => {
      if (!animal) return null;
      const firstPhoto = animal.photo || animal.primaryPhotoUrl || animal.primary_photo_url || animal?.photos?.[0]?.url;
      const computedAge = formatAnimalAge({
        birthDate: animal.birth_date ?? animal.birthDate,
        approxAgeMonths: animal.approx_age_months ?? animal.approxAgeMonths,
      });
      return {
        id: Number(animal.id),
        name: animal.name || animal.title,
        photo: firstPhoto,
        species_label: toText(animal.species_label || animal.species || animal.speciesName),
        size_label: toText(animal.size_label || animal.size || animal.sizeName),
        age: computedAge !== '-' ? computedAge : toText(animal.age || animal.approxAge || animal.age_label || animal.approxAgeText),
      };
    };

    const normalizeShelter = (shelter: any) => {
      if (!shelter) return null;
      return {
        id: Number(shelter.id),
        name: toText(shelter.name),
      };
    };

    const load = async () => {
      setIsLoading(true);
      try {
        const profile: any = await adoptionApi.getMyProfile();
        const profileId = profile?.id;
        let apps: any[] = profile?.applications || [];

        const needsRelations = apps.some((app: any) => !(app as any).animal || !(app as any).shelter);

        if ((!apps.length || needsRelations) && profileId) {
          const filter = {
            where: { adopterProfileId: Number(profileId) },
            order: ['createdAt DESC'],
            include: [
              { relation: 'animal', scope: { fields: ['id', 'name', 'statusId', 'photo', 'primaryPhotoUrl'] } },
              { relation: 'shelter', scope: { fields: ['id', 'name'] } },
            ],
          };
          apps = await adoptionApi.listApplications({ filter });
        }

        const enriched = await Promise.all(
          apps.map(async (app) => {
            const existingAnimal = (app as any).animal as ApplicationRow['animal'];
            let animal = normalizeAnimal(existingAnimal);

            if (!animal && app.animalId) {
              try {
                const animalData = await animalsPublicApi.getWithPhotos(app.animalId);
                animal = normalizeAnimal(animalData);
              } catch {
                animal = null;
              }
            }

            const shelter = normalizeShelter((app as any).shelter);

            return { ...app, animal, shelter };
          })
        );
        if (!cancelled) setApplications(enriched);
      } catch {
        if (!cancelled) setApplications([]);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  const sortedApplications = useMemo(
    () =>
      [...applications].sort((a, b) => {
        const aDate = a.createdAt ? new Date(a.createdAt).getTime() : 0;
        const bDate = b.createdAt ? new Date(b.createdAt).getTime() : 0;
        return bDate - aDate;
      }),
    [applications]
  );

  const openDetail = async (app: ApplicationRow) => {
    setSelected(app);
    setIsDetailOpen(true);
    setIsDetailLoading(true);
    try {
      const [full, tl] = await Promise.all([
        adoptionApi.getApplicationById(app.id),
        adoptionApi.getApplicationTimeline(app.id).catch(() => []),
      ]);
      setSelected({ ...app, ...(full as any) });
      setTimeline(Array.isArray(tl) ? tl : []);
      const response =
        (full as any).formResponse ||
        (full as any).adoptionFormResponse ||
        (full as any).form_response ||
        null;
      setFormResponse(response);
    } catch {
      setTimeline([]);
      setFormResponse(null);
    } finally {
      setIsDetailLoading(false);
    }
  };

  const renderAnswers = () => {
    if (!formResponse?.answersJson) {
      return (
        <div className="text-sm text-muted-foreground">
          No encontramos respuestas asociadas a esta solicitud.
        </div>
      );
    }

    const templateSections: any[] =
      (formResponse.template || (formResponse as any).formTemplate)?.schemaJson?.sections || [];

    const getFieldDefinition = (fieldId: string) => {
      for (const section of templateSections) {
        const found = (section.fields || []).find((f: any) => f.id === fieldId);
        if (found) return found;
      }
      return null;
    };

    const getLookupLabel = (lookupType: string | undefined, raw: any) => {
      if (!lookupType || !lookupSnapshot) return undefined;
      const id = Number(raw);

      const getCollection = () => {
        switch (lookupType) {
          case 'species': return lookupSnapshot.species;
          case 'sex': return lookupSnapshot.sexes;
          case 'size': return lookupSnapshot.sizes;
          case 'breed': return lookupSnapshot.breeds;
          case 'color': return lookupSnapshot.colors;
          case 'status': return lookupSnapshot.statuses;
          default: return undefined;
        }
      };

      const collection = getCollection();
      const item = collection?.find((i: any) => Number(i.id) === id);
      return item ? toOptionLabel(item) : undefined;
    };

    const formatValue = (key: string, field: any, value: any) => {
      if (value === undefined || value === null || value === '') return '—';
      if (Array.isArray(value)) return value.join(' - ');
      if (typeof value === 'boolean') return value ? 'Sí' : 'No';

      if (field?.type === 'city' || key === 'city' || key === 'municipio') {
        const id = Number(value);
        const city = lookupSnapshot?.cities?.find((c: any) => Number(c.id) === id);
        if (city) {
          const label = city.label || city.name || city.title || city.city;
          const dept = city.department || city.state || city.region;
          return label ? (dept ? `${label} - ${dept}` : label) : String(value);
        }
      }

      const match = field?.options?.find((opt: any) => String(opt.value) === String(value));
      if (match) return match.label;

      if (field?.lookupType) {
        const lookupLabel = getLookupLabel(field.lookupType, value);
        if (lookupLabel) return lookupLabel;
      }

      if (field?.type === 'radio' && (value === 'true' || value === 'false')) {
        return value === 'true' ? 'Sí' : 'No';
      }

      return String(value);
    };

    return (
      <div className="grid gap-3 sm:grid-cols-2">
        {Object.entries(formResponse.answersJson).map(([key, value]) => {
          const field = getFieldDefinition(key);
          const label = field?.label || field?.title || KNOWN_LABELS[key] || key;
          return (
            <div key={key} className="text-sm">
              <p className="text-muted-foreground">{label}</p>
              <p className="font-medium">
                {formatValue(key, field, value)}
              </p>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <Layout>
      <div className="container mx-auto px-4 py-8 max-w-6xl space-y-8">
        <div className="flex flex-col gap-3">
          <div className="flex items-center gap-3">
            <PawPrint className="h-6 w-6 text-primary" />
            <div>
              <h1 className="text-3xl font-bold tracking-tight">Mis adopciones</h1>
              <p className="text-muted-foreground text-sm">
                Aquí encontrarás el estado de tus solicitudes y podrás continuar buscando al compañero ideal.
              </p>
            </div>
          </div>

          <Card className="p-4 bg-muted/50">
            <ul className="list-disc pl-5 space-y-1 text-sm text-muted-foreground">
              <li>Las solicitudes son revisadas por la entidad responsable. Te notificaremos por aquí y por email.</li>
              <li>Si necesitamos información adicional, nos comunicaremos por correo o celular.</li>
              <li>Usa el botón para seguir buscando animales según tus preferencias.</li>
            </ul>
          </Card>

          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <Button onClick={() => navigate('/adopt')} className="w-full sm:w-auto">
              <Search className="mr-2 h-4 w-4" />
              Buscar animales según mis preferencias
            </Button>
            <Button variant="outline" onClick={() => navigate('/profile')} className="w-full sm:w-auto">
              <Settings2 className="mr-2 h-4 w-4" />
              Cambiar contraseña y datos
            </Button>
          </div>
        </div>

        <Card className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold">Solicitudes de adopción</h2>
            <div className="text-sm text-muted-foreground">
              Total: {applications.length}
            </div>
          </div>

          {isLoading ? (
            <div className="py-10 text-center text-muted-foreground">Cargando solicitudes...</div>
          ) : sortedApplications.length === 0 ? (
            <div className="py-10 text-center space-y-3">
              <p className="text-muted-foreground">Aún no tienes solicitudes de adopción.</p>
              <Button onClick={() => navigate('/adopt')}>
                <Search className="mr-2 h-4 w-4" />
                Buscar animales
              </Button>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Animal</TableHead>
                  <TableHead className="hidden sm:table-cell">Detalles</TableHead>
                  <TableHead>Abrigo</TableHead>
                  <TableHead>Fecha</TableHead>
                  <TableHead>Estado</TableHead>
                  <TableHead>Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sortedApplications.map((app) => (
                  <TableRow key={app.id}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        {app.animal?.photo ? (
                          <PhotoThumb
                            src={/^https?:\/\//i.test(app.animal.photo) ? app.animal.photo : `${import.meta.env.VITE_API_URL}${app.animal.photo}`}
                            alt={app.animal?.name || 'Animal'}
                            className="w-12 h-12 rounded-md"
                          />
                        ) : (
                          <div className="w-12 h-12 rounded-md bg-muted flex items-center justify-center">
                            <PawPrint className="h-5 w-5 text-muted-foreground" />
                          </div>
                        )}
                        <div>
                          <p className="font-medium">{app.animal?.name || `Animal #${app.animalId}`}</p>
                          <p className="text-xs text-muted-foreground">
                            ID solicitud #{app.id}
                          </p>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="hidden sm:table-cell">
                      <div className="text-sm text-muted-foreground space-y-1">
                        {app.animal?.species_label && <div>{app.animal.species_label}</div>}
                        {app.animal?.size_label && <div>Tamaño: {app.animal.size_label}</div>}
                        {app.animal?.age && <div>Edad: {app.animal.age}</div>}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="text-sm text-muted-foreground">
                        {app.shelter?.name || '—'}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2 text-sm">
                        <CalendarDays className="h-4 w-4 text-muted-foreground" />
                        <span>{app.createdAt ? new Date(app.createdAt).toLocaleDateString() : '—'}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge className={STATUS_COLORS[app.status]}>
                        {APPLICATION_STATUS_LABELS[app.status] || app.status}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Button variant="outline" size="sm" onClick={() => openDetail(app)}>
                        Ver detalle
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </Card>
      </div>

      <Dialog open={isDetailOpen} onOpenChange={setIsDetailOpen}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Detalle de solicitud #{selected?.id}</DialogTitle>
          </DialogHeader>

          {isDetailLoading ? (
            <div className="py-6 text-muted-foreground text-sm">Cargando detalles...</div>
          ) : !selected ? (
            <div className="py-6 text-muted-foreground text-sm">Selecciona una solicitud para ver detalles.</div>
          ) : (
            <div className="space-y-6">
              <div className="flex flex-col gap-2">
                <div className="flex flex-wrap items-center gap-2">
                  <Badge className={STATUS_COLORS[selected.status]}>
                    {APPLICATION_STATUS_LABELS[selected.status] || selected.status}
                  </Badge>
                  {selected.createdAt && (
                    <span className="text-sm text-muted-foreground">
                      Enviada el {new Date(selected.createdAt).toLocaleString()}
                    </span>
                  )}
                </div>
                {selected.animal && (
                  <div className="flex flex-col gap-3">
                    <div className="flex items-center gap-3">
                      {selected.animal.photo ? (
                        <PhotoThumb
                          src={/^https?:\/\//i.test(selected.animal.photo) ? selected.animal.photo : `${import.meta.env.VITE_API_URL}${selected.animal.photo}`}
                          alt={selected.animal.name || 'Animal'}
                          className="w-16 h-16 rounded-lg"
                        />
                      ) : (
                        <div className="w-16 h-16 rounded-lg bg-muted flex items-center justify-center">
                          <PawPrint className="h-5 w-5 text-muted-foreground" />
                        </div>
                      )}
                      <div>
                        <p className="font-semibold text-lg">{selected.animal.name || `Animal #${selected.animal.id}`}</p>
                        <p className="text-sm text-muted-foreground">
                          {selected.animal.species_label || ''} {selected.animal.size_label ? `· ${selected.animal.size_label}` : ''}
                        </p>
                      </div>
                    </div>

                    {/* Additional Animal Details */}
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 bg-muted/30 p-3 rounded-lg border">
                      <div className="space-y-1">
                        <span className="text-xs text-muted-foreground uppercase tracking-wider font-semibold">Sexo</span>
                        <p className="text-sm font-medium">{(selected.animal as any).sex?.label || '—'}</p>
                      </div>
                      <div className="space-y-1">
                        <span className="text-xs text-muted-foreground uppercase tracking-wider font-semibold">Edad</span>
                        <p className="text-sm font-medium">
                          {(selected.animal as any).approxAgeMonths ? `${(selected.animal as any).approxAgeMonths} meses` : '—'}
                        </p>
                      </div>
                      <div className="space-y-1">
                        <span className="text-xs text-muted-foreground uppercase tracking-wider font-semibold">Color</span>
                        <p className="text-sm font-medium">{(selected.animal as any).color?.label || '—'}</p>
                      </div>
                      <div className="space-y-1">
                        <span className="text-xs text-muted-foreground uppercase tracking-wider font-semibold">Necesidades</span>
                        <p className="text-sm font-medium truncate" title={(selected.animal as any).animalNeeds}>
                          {(selected.animal as any).animalNeeds || 'Ninguna específica'}
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <Card className="p-4">
                <h4 className="font-semibold mb-2">Formulario</h4>
                {renderAnswers()}
              </Card>

              <Card className="p-4">
                <h4 className="font-semibold mb-4">Timeline</h4>
                <Timeline events={timeline} />
              </Card>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
